import { Box, Tooltip, Typography } from "@mui/material";

import { BotOptionSpec, BotRegistryItem } from "features/adminUsers/adminUsers";
import {
  parseOptions,
  readListOption,
  readMapOption,
  readOption,
} from "utils/additionalOptions";

/** One row of the read-only options view: what Edit job shows as a field. */
export interface BotOptionLine {
  key: string;
  label: string;
  value: string;
  help?: string;
  /** An explicit value is stored on the job; false means default or empty. */
  set: boolean;
  /** Declared by the bot's registry entry; undeclared keys are hand-written. */
  declared: boolean;
  /** Declared as required and nothing is stored. */
  missing: boolean;
}

// Keys the panel renders through dedicated controls (the error-mode switch,
// the SSN side flags the backend derives), never as an option row.
const HIDDEN_OPTION_KEYS = [
  "SKIP_ERROR_EMPLOYEES",
  "IS_SSN_FOR_UPLOAD_ON_PAYCOM",
  "IS_SSN_FOR_UPLOAD_ON_UKG",
  "IS_FOR_UPLOAD",
];

export function botOptionSpecsFor(
  registry: { results?: BotRegistryItem[] } | BotRegistryItem[] | undefined | null,
  botName: string,
): BotOptionSpec[] {
  const items = Array.isArray(registry) ? registry : registry?.results ?? [];
  const match = items.find((row) => `${row.job_kind}.${row.bot_slug}` === botName);
  return match?.metadata?.options ?? [];
}

function describeSpec(spec: BotOptionSpec, raw: string): BotOptionLine {
  const stored = readOption(raw, spec.key);
  let set = stored != null && stored.trim() !== "";
  let value = "";
  if (spec.type === "list") {
    const items = readListOption(raw, spec.key);
    set = items.length > 0;
    value = items.join(", ");
  } else if (spec.type === "map") {
    const entries = Object.entries(readMapOption(raw, spec.key));
    set = entries.length > 0;
    value = entries.map(([k, v]) => `${k} -> ${v}`).join("; ");
  } else if (spec.type === "bool") {
    if (set) value = (stored ?? "").toLowerCase() === "true" ? "on" : "off";
    else if (spec.default != null) value = `${spec.default ? "on" : "off"} (default)`;
  } else if (set) {
    value = (stored ?? "").trim();
  } else if (spec.default != null && String(spec.default) !== "") {
    value = `${spec.default} (default)`;
  }
  const missing = !set && Boolean(spec.required) && !value;
  if (!value) value = missing ? "missing (required)" : "not set";
  return { key: spec.key, label: spec.label, value, help: spec.help, set, declared: true, missing };
}

/** Every option the bot declares, valued from `raw`, then any hand-written key. */
export function summarizeBotOptions(
  specs: BotOptionSpec[],
  raw: string | null | undefined,
  omitKeys: string[] = [],
): BotOptionLine[] {
  const text = raw ?? "";
  const omitted = new Set([...HIDDEN_OPTION_KEYS, ...omitKeys].map((k) => k.toUpperCase()));
  const declared = new Set(specs.map((spec) => spec.key.toUpperCase()));
  const lines = specs
    .filter((spec) => !omitted.has(spec.key.toUpperCase()))
    .map((spec) => describeSpec(spec, text));
  for (const token of parseOptions(text)) {
    const key = token.key.toUpperCase();
    if (!token.key || omitted.has(key) || declared.has(key)) continue;
    lines.push({
      key: token.key,
      label: token.key,
      value: token.value || "(flag)",
      set: true,
      declared: false,
      missing: false,
    });
  }
  return lines;
}

export function botOptionTagText(line: BotOptionLine): string {
  return `${line.label}: ${line.value}`;
}

const LABEL_SX = {
  display: "block",
  lineHeight: 1.2,
  letterSpacing: 0.06,
  fontWeight: 700,
  fontSize: 10,
  color: "text.secondary",
} as const;

export default function BotOptionSummary({
  lines,
  dense = false,
}: {
  lines: BotOptionLine[];
  dense?: boolean;
}) {
  if (!lines.length) return null;
  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: {
          xs: "1fr",
          sm: "repeat(2, minmax(0, 1fr))",
          md: dense ? "repeat(2, minmax(0, 1fr))" : "repeat(3, minmax(0, 1fr))",
        },
        gap: dense ? 1 : 1.5,
      }}
    >
      {lines.map((line) => {
        const label = (
          <Typography variant="overline" sx={LABEL_SX}>
            {line.label}
          </Typography>
        );
        return (
          <Box key={line.key} sx={{ minWidth: 0 }}>
            {line.help ? (
              <Tooltip title={line.help} placement="top-start">
                <Box sx={{ width: "fit-content", cursor: "help" }}>{label}</Box>
              </Tooltip>
            ) : (
              label
            )}
            <Typography
              variant="body2"
              sx={{
                mt: 0.25,
                overflowWrap: "anywhere",
                fontFamily: line.declared ? undefined : "monospace",
                fontSize: line.declared ? undefined : 12,
                color: line.missing ? "error.main" : line.set ? "text.primary" : "text.secondary",
              }}
            >
              {line.value}
            </Typography>
          </Box>
        );
      })}
    </Box>
  );
}
